import DeleteIcon from '@mui/icons-material/Delete'
import { Box, Button, Dialog, Tooltip, Typography, useTheme } from '@mui/material'
import PopupState from 'material-ui-popup-state'
import { bindDialog, bindTrigger } from 'material-ui-popup-state/hooks'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SquareButton, StyledButtonPrimary } from '../../styles/ButtonStyles'
import dayjs from 'dayjs'
import { OnlyPastDatesTopography } from '../DatesTopography/OnlyPastDatesTopography'
import { MixDatesTopography } from '../DatesTopography/MixDatesTopography'
import { ValidDatesTopography } from '../DatesTopography/ValidDatesTopography'

type DeletePopupProps = {
  selectedRows: string[]
  onDeleteAction: (e: React.MouseEvent<HTMLElement>, selectedSections: string[]) => void
  isDisabled: boolean
  isPlanned?: boolean
  isAllEmpty?: boolean
}

const validateSelectedRows = (selectedRows: string[]) => {
  let futureSelectedRows: string[] = []
  let pastSelectedRows: string[] = []

  selectedRows.forEach(date => {
    if (dayjs(date).isBefore(dayjs(), 'day')) {
      pastSelectedRows.push(date)
    } else {
      futureSelectedRows.push(date)
    }
  })

  pastSelectedRows.sort((a, b) => (dayjs(a).isBefore(dayjs(b)) ? -1 : 1))
  futureSelectedRows.sort((a, b) => (dayjs(a).isBefore(dayjs(b)) ? -1 : 1))

  return { futureSelectedRows, pastSelectedRows }
}

const sortDates = (dates: string[]) => {
  return dates.sort((a, b) => (dayjs(a).isBefore(dayjs(b)) ? -1 : 1))
}

const ScheduleDeletePopup = ({ selectedRows, onDeleteAction, isDisabled, isPlanned, isAllEmpty }: DeletePopupProps) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const [futureSelectedRows, setFutureSelectedRows] = useState<string[]>([])
  const [pastSelectedRows, setPastSelectedRows] = useState<string[]>([])
  const [isValidDates, setIsValidDates] = useState<boolean>(false)
  const [isOnlyPastDates, setIsOnlyPastDates] = useState<boolean>(false)
  const [sortedSelectedRows, setSortedSelectedRows] = useState<string[]>([])

  useEffect(() => {
    if (isPlanned) {
      const { futureSelectedRows, pastSelectedRows } = validateSelectedRows(selectedRows)
      setFutureSelectedRows(sortDates(futureSelectedRows))
      setPastSelectedRows(sortDates(pastSelectedRows))
      setSortedSelectedRows(sortDates(selectedRows))
    } else {
      setSortedSelectedRows(sortDates(selectedRows))
    }
  }, [selectedRows, isPlanned])

  useEffect(() => {
    setIsValidDates(!isPlanned || pastSelectedRows.length === 0)
    setIsOnlyPastDates(!!isPlanned && futureSelectedRows.length === 0)
  }, [isPlanned, pastSelectedRows, futureSelectedRows])

  return (
    <PopupState variant="popover" popupId="demo-popup-menu" disableAutoFocus={false} parentPopupState={null}>
      {popupState => (
        <React.Fragment>
          <Tooltip title={<Typography variant="caption">{t('components.tooltip.deleteSelectedRow')}</Typography>}>
            <span>
              <SquareButton
                aria-label={t('components.tooltip.deleteSelectedRow')}
                disabled={isDisabled}
                color="primary"
                {...bindTrigger(popupState)}
                sx={{
                  backgroundColor: theme.palette.primary.light,
                  width: { xs: 28, sm: 32, md: 32 },
                  height: { xs: 28, sm: 32, md: 32 },
                }}
              >
                <DeleteIcon sx={{ width: 24, height: 24 }} />
              </SquareButton>
            </span>
          </Tooltip>
          <Dialog {...bindDialog(popupState)}>
            <Box
              sx={{
                maxWidth: '720px',
                minWidth: '250px',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="h3">{t('page.schedule.deleteManyPopup.delete')}</Typography>
              {isAllEmpty ? (
                <>
                  <Typography sx={{ pb: 2 }} variant="body1">
                    {t('page.schedule.deleteManyPopup.nothingToDelete')}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                    <StyledButtonPrimary
                      onClick={popupState.close}
                      sx={{ fontWeight: 'bold', px: 5 }}
                      variant="contained"
                      size="medium"
                    >
                      {t('components.button.leave')}
                    </StyledButtonPrimary>
                  </Box>
                </>
              ) : (
                <>
                  {isValidDates && <ValidDatesTopography selectedRows={sortedSelectedRows} />}
                  {isOnlyPastDates && <OnlyPastDatesTopography selectedRows={sortedSelectedRows} />}
                  {!isValidDates && !isOnlyPastDates && (
                    <MixDatesTopography futureSelectedRows={futureSelectedRows} pastSelectedRows={pastSelectedRows} />
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                    {!isOnlyPastDates && (
                      <Button sx={{ color: 'black', fontWeight: 'bold', px: 5, mr: 2 }} onClick={popupState.close}>
                        {t('components.button.leave')}
                      </Button>
                    )}
                    <StyledButtonPrimary
                      onClick={async e => {
                        if (!isOnlyPastDates) {
                          onDeleteAction(e, isValidDates ? sortedSelectedRows : futureSelectedRows)
                        }
                        popupState.close()
                      }}
                      sx={{ fontWeight: 'bold', px: 5 }}
                      variant="contained"
                      size="medium"
                    >
                      {!isOnlyPastDates || isAllEmpty ? t('components.button.apply') : t('components.button.leave')}
                    </StyledButtonPrimary>
                  </Box>
                </>
              )}
            </Box>
          </Dialog>
        </React.Fragment>
      )}
    </PopupState>
  )
}

export default ScheduleDeletePopup
